import React, { useContext, useEffect } from 'react';
import { Box, IconButton } from '@material-ui/core';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import ApiContext from '../../../api/context';
import classes from './LanguageSwitch.module.css';
import deLang from './deLang.svg';
import enLang from './enLang.svg';
import { useLocation, useNavigate } from '@reach/router';
import { getLanguageChangePageProductRedirect, getLanguageChangePageRedirect } from '../../../utils/pages';
import { SupportedLanguages } from '../../../enums';
import { useDispatch, useSelector } from 'react-redux';
import { UserSlice } from '../../../Auth/store';
import { selectProductItems } from 'Product/store/selectors';

const isLanguageEnabled = process.env.REACT_APP_ENABLE_LANGUAGE_TOGGLE === 'true';

interface CommonLanguageSwitchProps {
  onChange?(): void;
}

export default function CommonLanguageSwitch({ onChange }: CommonLanguageSwitchProps) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { updateLanguage } = UserSlice.actions;
  const { href: currentPath , search} = useLocation();
  const api = useContext(ApiContext);
  const products = useSelector(selectProductItems);
  const isProductPage = currentPath.includes('/product/');
  const params = new URLSearchParams(search);
  const isDe = i18n.language === 'de';

  useEffect(() => {
    if (params.get('lang')) {
      toggleLanguage(params.get('lang') !== 'en' ? SupportedLanguages.de : SupportedLanguages.en);
    }
  }, []);

  async function toggleLanguage(language: SupportedLanguages) {
    const prevLanguage = (i18n.language === 'de' ? 'de' : 'en') as 'de' | 'en';
    const nextLanguage = (language === SupportedLanguages.de ? 'de' : 'en') as 'de' | 'en';

    await i18n.changeLanguage(language);
    Cookies.set('language', language, { expires: 30 });
    if (window.OneTrust) {
      await window.OneTrust.changeLanguage(language);
    }
    await api.auth.changePreferredLanguage(language);
    dispatch(updateLanguage(language));
    onChange && onChange();
    const path = isProductPage
      ? getLanguageChangePageProductRedirect(prevLanguage, nextLanguage, window.location.pathname, products)
      : getLanguageChangePageRedirect(prevLanguage, nextLanguage, window.location.pathname);

    // Keep the query (e.g. ?offerId=) but drop `lang`, which would force the language back on mount.
    const query = new URLSearchParams(window.location.search);
    query.delete('lang');
    const search = !path.includes('?') && query.toString() ? `?${query}` : '';

    if (path.length > 1) {
      await navigate(path + search);
    }
  }

  if (!isLanguageEnabled) {
    return null;
  }

  return (
    <Box display="flex" justifyContent="space-evenly">
      <IconButton className={`${classes.langBtn} ${isDe ? classes.active : classes.inactive}`} onClick={() => toggleLanguage(SupportedLanguages.de)}>
        <img alt="Deutsch" width={23} height={23} src={deLang} />
      </IconButton>
      <IconButton className={`${classes.langBtn} ${!isDe ? classes.active : classes.inactive}`} onClick={() => toggleLanguage(SupportedLanguages.en)}>
        <img alt="English" width={23} height={23} src={enLang} />
      </IconButton>
    </Box>
  );
}
